<!-- @format -->
<template>
    <app-widget ref="widget" :widget="widget" class="add-custom-field-widget">
        <template #headerTitle>
            <h3>Add Custom Field</h3>
        </template>

        <div class="field-containers">
            <div
                class="field-container"
                :class="{
                    'field-container-error': submitted && $v.selectedCategory.$error,
                }"
            >
                <label>Field Category</label>
                <app-input-radio
                    :options="categoryOptions"
                    :value="selectedCategory"
                    class="radio-button-group"
                    @input="selectCategory"
                />
                <div v-if="submitted && $v.selectedCategory.$error" class="error-message">
                    Field Category is required.
                </div>
            </div>

            <div class="field-container">
                <label>Field Type</label>
                <div class="input-type-display">
                    <custom-field-type-icon :type="inputType" />
                    <span>{{ inputTypeLabel }}</span>
                </div>
            </div>

            <div
                class="field-container"
                :class="{
                    'field-container-error': submitted && $v.name.$error,
                }"
            >
                <label>Field Name</label>
                <app-input
                    v-model="name"
                    placeholder="Field Name"
                    :is-valid="!submitted || (submitted && !$v.name.$error)"
                />
                <div v-if="submitted && $v.name.$error" class="error-message">
                    Field Name is required.
                </div>
            </div>

            <div
                class="field-container"
                :class="{
                    'field-container-error': submitted && $v.description.$error,
                }"
            >
                <label>Description</label>
                <app-input
                    v-model="description"
                    placeholder="Write a description"
                    :is-valid="!submitted || (submitted && !$v.description.$error)"
                />
                <div v-if="submitted && $v.description.$error" class="error-message">
                    Description is required.
                </div>
            </div>

            <div class="custom-field-preview">
                <custom-field-table-preview :form="form" />
                <custom-field-field-preview :form="form" />
            </div>
        </div>

        <template #footer>
            <div>
                <app-button :disabled="saving" class="back-button" @click="close"
                    >Cancel</app-button
                >
            </div>
            <div>
                <app-button :loading="saving" @click="save">Save</app-button>
            </div>
        </template>
    </app-widget>
</template>

<script>
import EventBus from '@/scripts/event-bus';
import { CustomFieldInputTypeDefinitions } from '@/scripts/custom-fields';
import { required } from 'vuelidate/lib/validators';
import { CustomFieldService } from '@/scripts/services/custom-fields/custom-field-service';

const AppWidget = () => import('@/components/common/app-widget/app-widget.vue');
const AppInputRadio = () => import('@/components/common/app-input-radio.vue');
const AppInput = () => import('@/components/common/app-input.vue');
const AppButton = () => import('@/components/common/app-button.vue');
const CustomFieldTypeIcon = () =>
    import('@/components/sliding-widgets/custom-field-reusable/custom-field-type-icon.vue');
const CustomFieldTablePreview = () =>
    import('@/components/sliding-widgets/custom-field-reusable/custom-field-table-preview.vue');
const CustomFieldFieldPreview = () =>
    import('@/components/sliding-widgets/custom-field-reusable/custom-field-field-preview.vue');

export default {
    name: 'add-custom-field-widget',

    components: {
        'app-widget': AppWidget,
        'app-input-radio': AppInputRadio,
        'app-button': AppButton,
        'app-input': AppInput,
        'custom-field-type-icon': CustomFieldTypeIcon,
        'custom-field-table-preview': CustomFieldTablePreview,
        'custom-field-field-preview': CustomFieldFieldPreview,
    },

    props: {
        widget: {
            required: true,
            type: Object,
        },
    },

    data() {
        return {
            selectedCategory: null,
            name: '',
            description: '',
            submitted: false,
            saving: false,
        };
    },

    computed: {
        clientId() {
            return this.widget.clientId;
        },

        categories() {
            return this.widget.categories;
        },

        categoryOptions() {
            return this.categories.map((category) => {
                return {
                    name: category.label,
                    value: category.value,
                };
            });
        },

        inputType() {
            return this.widget.inputType;
        },

        inputTypeDefinition() {
            return CustomFieldInputTypeDefinitions[this.inputType];
        },

        inputTypeLabel() {
            return this.inputTypeDefinition.label;
        },

        form() {
            const inputType = {
                name: this.inputTypeDefinition.label,
                value: this.inputType,
            };

            return {
                name: this.name,
                description: this.description,
                inputType,
                inputValue: [],
                value: '',
            };
        },
    },

    validations: {
        selectedCategory: {
            required,
        },

        name: {
            required,
        },

        description: {
            required,
        },
    },

    methods: {
        selectCategory(category) {
            this.selectedCategory = category;
        },

        async save() {
            this.submitted = true;

            this.$v.$touch();
            if (this.$v.$invalid) {
                setTimeout(() => {
                    this.$refs.widget.scrollTo('.field-container-error');
                });
                return;
            }

            await this.createCustomField();

            this.close();
        },

        async createCustomField() {
            try {
                this.saving = true;

                switch (this.selectedCategory.value) {
                    case 'company':
                        await CustomFieldService.createCompanyCustomField({
                            clientId: this.clientId,
                            name: this.name,
                            description: this.description,
                            inputType: this.inputType,
                        });
                        break;
                    case 'contact':
                        await CustomFieldService.createContactCustomField({
                            clientId: this.clientId,
                            name: this.name,
                            description: this.description,
                            inputType: this.inputType,
                        });
                        break;
                    default:
                        throw new Error(`Invalid category: ${this.selectedCategory.value}`);
                }

                this.$helpers.showSuccessNotification(
                    'Your custom field has been created successfully.',
                );

                EventBus.$emit('custom-field-added');
            } catch (error) {
                this.$helpers.showFailNotification(
                    'An error occurred while creating your custom field.',
                    { error },
                );
            } finally {
                this.saving = false;
            }
        },

        close() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.add-custom-field-widget .content-wrapper {
    padding: 24px !important;
}

.add-custom-field-widget .field-containers {
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.add-custom-field-widget .field-container {
    display: flex;
    flex-direction: column;
}

.add-custom-field-widget .field-container > label {
    margin-bottom: 8px;
}

.add-custom-field-widget .error-message {
    margin-top: 8px;
    font-size: 12px;
    color: var(--color-text-warning);
}

.add-custom-field-widget .custom-field-preview {
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.add-custom-field-widget .input-type-display {
    display: flex;
    align-items: center;
    gap: 8px;
}

.add-custom-field-widget .radio-button-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.add-custom-field-widget .radio-button-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.add-custom-field-widget .radio-button-group .option {
    margin-bottom: 0;
}

.add-custom-field-widget .radio-button-group .option .radio-option-label {
    color: var(--color-text-default);
    font-weight: 500;
}
</style>
